import {Dispatch, ReactNode, SetStateAction, useState} from 'react';
import {yupResolver} from '@hookform/resolvers/yup';
import {ApiError, apiErrorCodes} from '@legit.health/backend-client';
import {
  BaseUser,
  BodySite,
  Condition,
  ScoringSystemItem,
  ScoringSystem,
  TextWithIcon,
  generatePath,
  generateRandomId
} from '@legit.health/ui';
import {Alert, Box, Button, CircularProgress} from '@mui/material';
import {TFunction} from 'i18next';
import {useForm} from 'react-hook-form';
import {useSharedDataContext} from '@/shared/components/layout/LoggedInLayout/contexts/SharedDataContext';
import {POST_FOLLOW_UP} from '@/shared/const/backend';
import useApiMutation from '@/shared/hooks/useApiMutation';
import {AnamnesisQuestion} from '@/shared/interfaces/AnamnesisQuestion';
import AnamnesisStep from '../../steps/AnamnesisStep';
import DiqaErrors from '../../steps/DiqaErrors';
import {ConditionInfo, CreateDiagnosticReportResponse} from '../../types';
import anamnesisSessionStorage from '../../utils/anamnesisSessionStorage';
import InstructionsStep from './components/Instructions';
import Questionnaires from './components/ScoringSystems';
import UploadMedia from './components/UploadMedia';
import {FormValues} from './types';
import createSchema from './utils/createSchema';
import getScoringSystemsAnswers from './utils/getScoringSystemsAnswers';
import {
  mapImageFormValuesToApiFormat,
  mapVideoFormValuesToApiFormat
} from './utils/mapFormValuesToApiFormat';

export default function FollowUpForm({
  anamnesisQuestions,
  currentStep,
  condition,
  conditionInfo,
  extraPostData,
  isForClinicalTrials,
  setCurrentStep,
  onSuccess,
  patient,
  renderItemLabel,
  steps,
  t,
  visitIdentifier,
  withTemporaryImages
}: {
  anamnesisQuestions: AnamnesisQuestion[];
  condition: Condition;
  conditionInfo: ConditionInfo | null;
  currentStep: number;
  extraPostData?: (conditionId: string | null) => Record<string, any>;
  isForClinicalTrials: boolean;
  onSuccess: (values: CreateDiagnosticReportResponse) => void;
  patient: BaseUser;
  renderItemLabel?: (options: {
    item: ScoringSystemItem;
    scoringSystem: ScoringSystem;
    bodySite: BodySite;
  }) => string;
  setCurrentStep: Dispatch<SetStateAction<number>>;
  steps: {label: string; icon: ReactNode}[];
  t: TFunction;
  visitIdentifier: string;
  withTemporaryImages: boolean;
}) {
  const {scoringSystemsById} = useSharedDataContext();
  const [postFollowUpError, setPostFollowUpError] = useState<ApiError | null>(null);
  const form = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      image: {
        id: generateRandomId(),
        crop: null,
        file: null
      },
      video: null,
      conditionId: `${condition.id}`,
      patientId: patient.id,
      visitIdentifier,
      anamnesisQuestions:
        anamnesisSessionStorage.get() ??
        anamnesisQuestions.reduce(
          (acc, aq) => ({
            ...acc,
            [aq.id]: ''
          }),
          {}
        ),
      scoringSystems: conditionInfo
        ? getScoringSystemsAnswers({conditionInfo, scoringSystemsById})
        : {},
      perspective: null,
      ...extraPostData
    },
    resolver: yupResolver(createSchema({condition, conditionInfo, scoringSystemsById}))
  });

  const {
    mutate: postDiagnosticReportRequest,
    isPending,
    isError
  } = useApiMutation<CreateDiagnosticReportResponse>();

  async function handleSubmit(formValues: FormValues) {
    try {
      const postData = formValues.video
        ? await mapVideoFormValuesToApiFormat(formValues)
        : await mapImageFormValuesToApiFormat(formValues);

      postDiagnosticReportRequest(
        {
          method: 'POST',
          path: generatePath(POST_FOLLOW_UP),
          body: {
            ...postData,
            ...(extraPostData ? extraPostData(formValues.conditionId) : {})
          }
        },
        {
          onSuccess: function (response) {
            onSuccess(response.data);
          },
          onError: function (error) {
            setPostFollowUpError(error);
          }
        }
      );
    } catch (error) {
      setPostFollowUpError(
        new ApiError(400, {
          data: null,
          errorCode: (error as Error).message,
          message: (error as Error).message,
          success: false
        })
      );
    }
  }

  let node = null;
  const stepNumberModifier = isForClinicalTrials ? 1 : 0;

  const isImageError =
    postFollowUpError?.content.errorCode === apiErrorCodes.medicalDevice.invalidImage;

  if (currentStep === 1 - stepNumberModifier) {
    node = (
      <AnamnesisStep
        anamnesisQuestions={anamnesisQuestions}
        value={form.getValues('anamnesisQuestions')}
        onBack={() => setCurrentStep((s) => s - 1)}
        onNext={function (value) {
          form.setValue('anamnesisQuestions', value);
          setCurrentStep((s) => s + 1);
        }}
        t={t}
        activeStep={1}
        totalSteps={steps.length}
      />
    );
  } else if (currentStep === 2 - stepNumberModifier) {
    node = (
      <InstructionsStep
        onBack={() => setCurrentStep((s) => s - 1)}
        onNext={() => setCurrentStep((s) => s + 1)}
        t={t}
        activeStep={2 - stepNumberModifier}
        totalSteps={steps.length}
        perspective={form.getValues('perspective')}
        onSelectPerspective={(perspective) => form.setValue('perspective', perspective)}
        condition={condition}
        conditionInfo={conditionInfo}
      />
    );
  }
  if (isPending) {
    return (
      <Box
        sx={{
          maxWidth: 'md',
          display: 'flex',
          justifyContent: 'center',
          mx: 'auto'
        }}
      >
        <TextWithIcon
          sx={{
            color: 'primary.main'
          }}
          iconLeft={<CircularProgress sx={{mr: 1, color: 'primary.main'}} />}
        >
          {t('ui:sending')}
        </TextWithIcon>
      </Box>
    );
  } else if (isError && postFollowUpError) {
    return (
      <Box
        id="postFollowUpError"
        sx={{
          maxWidth: 'md',
          mx: 'auto'
        }}
      >
        {isImageError ? (
          <DiqaErrors
            t={t}
            onClickNewImage={() => {
              setCurrentStep(2 - stepNumberModifier);
              setPostFollowUpError(null);
            }}
            images={[form.getValues().image]}
            apiError={postFollowUpError}
          />
        ) : (
          <Box
            sx={{
              maxWidth: 'md'
            }}
          >
            <Alert severity="error" sx={{mb: 4}}>
              {t('shared/common:diagnosticReport.form.serverError')}
            </Alert>
            <Button
              variant="contained"
              color="primary"
              disableElevation
              fullWidth
              onClick={() => form.handleSubmit(handleSubmit)()}
            >
              {t('shared/common:tryAgain')}
            </Button>
          </Box>
        )}
      </Box>
    );
  } else if (currentStep === 4 - stepNumberModifier) {
    node = (
      <Questionnaires
        activeStep={4 - stepNumberModifier}
        condition={condition}
        conditionInfo={conditionInfo}
        form={form}
        onBack={() => setCurrentStep((s) => s - 1)}
        renderItemLabel={renderItemLabel}
        t={t}
        totalSteps={steps.length}
      />
    );
  } else if (currentStep === 3 - stepNumberModifier) {
    node = (
      <UploadMedia
        activeStep={3 - stepNumberModifier}
        isPending={isPending}
        condition={condition}
        conditionInfo={conditionInfo}
        form={form}
        onBack={() => setCurrentStep((s) => s - 1)}
        onChangeImage={(image) => form.setValue('image', image)}
        onChangeVideo={(video) => form.setValue('video', video)}
        patient={patient}
        onNext={() => setCurrentStep((s) => s + 1)}
        t={t}
        totalSteps={steps.length}
        withTemporaryImages={withTemporaryImages}
      />
    );
  }

  return (
    <div>
      <form onSubmit={form.handleSubmit(handleSubmit)}>{node}</form>
    </div>
  );
}
